import {
  Route,
  Switch,
} from 'react-router-dom';
import { t } from 'ttag';
import { useCallback, useMemo } from 'react';

import * as path from '@urls';
import * as NonSpaPath from '@legacyUrls';
import { ReviewThankPage } from '@pages/ReviewThankPage';
import CollectionRouterPreloader from '@components/CollectionRouterPreloader';
import { RouterPreloader } from '@components/RouterPreloader';
import { OutfitHeaderSkeleton } from '@components/OutfitHeaderSkeleton';
import { DeliveryFlowForSellersContainer } from '@containers/settings/DeliveryFlowForSellers/DeliveryFlowForSellers';
import { CatalogPageSkeleton } from '@pages/catalog/CatalogPage/CatalogPageSkeleton';
import { useAppContext } from '@contexts/AppContext';
import { withClientOnly, loadableWithFallback } from '@utils/loadable';

const useRoutes = () => {
  const { isBot } = useAppContext();
  const loadable = useCallback<typeof loadableWithFallback>(
    (loadFn, options) => loadableWithFallback(
      loadFn,
      {
        resolveComponent: (module) => {
          if (!('default' in module)) {
            throw new Error('Module should have default export');
          }

          const component = module.default;
          if (typeof options?.ssr !== 'undefined') {
            return options.ssr
              ? component
              : withClientOnly(component);
          }
          return isBot
            ? component
            : withClientOnly(component);
        },
        ...options,
        ssr: true,
      },
    ),
    [isBot],
  );

  return useMemo(
    () => ({
      AddNewPaymentCardPage: loadable(() => import('@pages/AddNewPaymentCardPage')),
      ClosedBraveLandingPage: loadable(() => import('@pages/ClosedBraveLandingPage')),
      CheckoutPaymentPage: loadable(() => import('@pages/CheckoutPaymentPage')),
      PromotionPaymentPage: loadable(() => import('@pages/PromotionPaymentPage')),
      BalanceRefillFormPage: loadable(() => import('@pages/BalanceRefillFormPage')),
      PaymentBalancePage: loadable(() => import('@pages/PaymentBalancePage')),
      SmartPaymentPage: loadable(() => import('@pages/SmartPaymentPage')),
      SettingsRoutes: loadable(() => (import('./settings'))),
      SocialRoutes: loadable(() => (import('./social'))),
      SocialAddPostThanks: loadable(() => (import('@pages/SocialAddPostThanks'))),
      ChatPage: loadable(() => import('@pages/ChatPage')),
      NewDialogThanksPage: loadable(() => import('@pages/NewDialogThanksPage')),
      CollectionPage: loadable(
        () => import('@pages/CollectionPage'),
        { fallback: <CollectionRouterPreloader /> },
      ),
      LooksCollectionPage: loadable(
        () => import('@pages/LooksCollectionPage'),
        { fallback: <OutfitHeaderSkeleton /> },
      ),
      LooksCollectionsPage: loadable(
        () => import('@pages/LooksCollectionsPage'),
      ),
      UserProfileRoutes: loadable(() => (import('./userProfile'))),
      CheckoutPaymentStatusPage: loadable(() => import('@pages/CheckoutPaymentStatusPage')),
      VirtualCatalogPage: loadable(() => import('@pages/catalog/VirtualCatalogPage')),
      MyReviewsPage: loadable(() => (import('@pages/MyReviewsPage'))),
      ProductReviewsPage: loadable(() => import('@pages/ProductReviewsPage')),
      ThanksAddProductPage: loadable(() => (import('@pages/ThanksAddProductPage'))),
      ThanksAddProductPageWithVerification: loadable(() => (import('@pages/ThanksAddProductPageWithVerification'))),
      MyProducts: loadable(() => (import('./myProducts'))),
      MyConfirmedPotentialDealsPage: loadable(() => (import('@pages/MyConfirmedPotentialDealsPage'))),
      MyPotentialDealsPage: loadable(() => (import('@pages/MyPotentialDealsPage'))),
      AuthRoutes: loadable(() => (import('./auth'))),
      CheckoutAuthRoutes: loadable(() => (import('./checkoutAuth'))),
      CheckoutAuthRedirectPage: loadable(() => (import('@pages/CheckoutAuthRedirectPage'))),
      NotificationsPage: loadable(() => (import('@pages/NotificationsPage'))),
      MyWalletPage: loadable(() => import('@pages/MyWalletPage')),
      CheckoutPage: loadable(() => import('@pages/CheckoutPage')),
      DiscountPromotionsPage: loadable(() => import('@pages/DiscountPromotionsPage')),
      DiscountPromotionsParticipatesPage: loadable(() => import('@pages/DiscountPromotionsParticipatesPage')),
      DiscountPromotionProductsListPage: loadable(() => import('@pages/DiscountPromotionProductsListPage')),
      ServicePaymentStatusPage: loadable(() => import('@pages/ServicePaymentStatusPage')),
      CheckoutSellerPage: loadable(() => import('@pages/CheckoutSellerPage')),
      MySalesPage: loadable(() => import('@pages/MySalesPage')),
      MyPurchasesPage: loadable(() => import('@pages/MyPurchasesPage')),
      ProductDetailsPage: loadable(() => import('@pages/ProductDetailsPage'), { ssr: true, fallback: <RouterPreloader /> }),
      CardVerificationCallbackPage: loadable(() => import('@pages/CardVerificationCallbackPage')),
      SubscribedBrandsProducts: loadable(() => (import('@pages/SubscribedBrandsProducts'))),
      SubscribedUsersProducts: loadable(() => (import('@pages/SubscribedUsersProducts'))),
      SubscribedSavedSearch: loadable(() => (import('@pages/SubscribedSavedSearch'))),
      RedirectToViewedPage: loadable(() => (import('@pages/RedirectToViewedPage'))),
      PhoneVerificationPage: loadable(() => import('@pages/PhoneVerificationPage')),
      CatalogReviews: loadable(() => import('@pages/CatalogReviews')),
      BankIdFailDuplicate: loadable(() => import('@pages/BankIdFailDuplicate')),
      BankIdFailVerification: loadable(() => import('@pages/BankIdFailVerification')),
      BankIdSuccessVerification: loadable(() => import('@pages/BankIdSuccessVerification')),
      MobileAppsLandingPage: loadable(() => import('@pages/landings/MobileAppsLandingPage')),
      SafePaymentLandingPage: loadable(() => import('@pages/landings/SafePaymentLandingPage')),
      SmartLandingPage: loadable(() => import('@pages/landings/SmartLandingPage')),
      SwapLandingPage: loadable(() => import('@pages/landings/SwapLandingPage')),

      // Always through the ssr, because this page may be opened by mobile clients which provide
      // auth token only for initial page request (not for subsequent graphql requests)
      ContactsPage: loadable(
        () => import('@pages/SecondaryPage'),
        { ssr: true },
      ),
      SecondaryPage: loadable(() => import('@pages/SecondaryPage')),
      PublicContractPage: loadable(() => import('@pages/PublicContractPage')),
      PromotionPaymentMethodsPage: loadable(() => (import('@pages/PromotionPaymentMethodsPage'))),
      PromotionsPage: loadable(() => import('@pages/PromotionsPage')),
      NewProductPromotionPackagesPage: loadable(() => import('@pages/NewProductPromotionPackagesPage')),
      CatalogPage: loadable(
        () => import('@pages/catalog/CatalogPage'),
        { fallback: <CatalogPageSkeleton /> },
      ),
      SellerAchievementsPage: loadable(() => import('@pages/SellerAchievementsPage')),
      MyMessageToSubscribersPage: loadable(() => import('@pages/MyMessageToSubscribersPage')),
      SwapNewOrderThanksPage: loadable(() => import('@pages/SwapNewOrderThanksPage')),
      SwapBuyerCheckoutPage: loadable(() => (import('@pages/SwapBuyerCheckoutPage'))),
      SwapSellerCheckoutPage: loadable(() => (import('@pages/SwapSellerCheckoutPage'))),
      BankAccountIdentificationPendingPage: loadable(() => import('@pages/BankAccountIdentificationPendingPage')),
      BankAccountIdentificationPage: loadable(() => import('@pages/BankAccountIdentificationPage')),
      MyFavoritesPage: loadable(() => import('@pages/MyFavoritesPage')),
      ProductFormPage: loadable(() => import('@pages/ProductFormPage')),
      SmartRoutes: loadable(() => (import('./smart'))),
      BrandsFeedPage: loadable(() => import('@pages/BrandsFeedPage')),
    }),
    [loadable],
  );
};

const Routes: React.FC = () => {
  const routes = useRoutes();

  return (
    <>
      <Switch>
        <Route
          path={path.dialogThanks}
          component={routes.NewDialogThanksPage}
        />
        <Route
          path={path.braveClosedPage}
          component={routes.ClosedBraveLandingPage}
        />

        <Route
          path={[
            // Important: should be first in list to provide thread id param
            path.dialogDetails,
            path.dialogsList,
          ]}
          component={routes.ChatPage}
        />
        <Route
          exact={true}
          path={path.memberReviewRequestThanks}
          component={ReviewThankPage}
        />
        <Route
          path={[
            path.memberReviewRequests,
            path.memberReview,
            path.memberReviewSeller,
            path.memberReviewBuyer,
          ]}
          component={routes.MyReviewsPage}
        />
        <Route
          path={path.memberProfile}
          component={routes.SettingsRoutes}
        />
        <Route
          path={path.mySuperSellerAchievements}
          component={routes.SellerAchievementsPage}
        />
        <Route
          path={path.myMessageToSubscribers}
          component={routes.MyMessageToSubscribersPage}
        />
        <Route
          path={path.publicMemberProfile}
          component={routes.UserProfileRoutes}
        />
        <Route
          path={path.addNewProductThanks}
          component={routes.ThanksAddProductPage}
        />
        <Route
          path={path.addNewProductThanksWithVerification}
          component={routes.ThanksAddProductPageWithVerification}
        />
        <Route
          path={path.myClothes}
          component={routes.MyProducts}
        />
        <Route
          path={path.myConfirmedPotentialDeals}
          component={routes.MyConfirmedPotentialDealsPage}
        />
        <Route
          path={path.myPotentialDeals}
          component={routes.MyPotentialDealsPage}
        />
        <Route
          path={path.login}
          component={routes.AuthRoutes}
        />
        <Route
          path={[
            path.notificationsDetails,
            path.notificationsList,
          ]}
          component={routes.NotificationsPage}
        />
        {/* Promotion */}
        <Route
          exact={true}
          path={path.addNewProductPromotionPackages}
          component={routes.NewProductPromotionPackagesPage}
        />
        <Route
          exact={true}
          path={path.promotions}
          component={routes.PromotionsPage}
        />
        <Route
          path={[
            path.promotionServicesPaymentMethods,
            path.promotionPackagesPaymentMethods,
          ]}
          exact={true}
          component={routes.PromotionPaymentMethodsPage}
        />
        <Route
          exact={true}
          path={[
            path.promotionServicesPayment,
            path.promotionPackagesPayment,
          ]}
          component={routes.PromotionPaymentPage}
        />
        <Route
          exact={true}
          path={[
            path.promotionServicesPaymentAnotherCard,
            path.promotionPackagesPaymentAnotherCard,
          ]}
          component={() => <routes.PromotionPaymentPage withCardFormOnly={true} />}
        />

        {/* PaymentBalance */}
        <Route
          exact={true}
          path={path.balanceRefillForm}
          component={routes.BalanceRefillFormPage}
        />
        <Route
          exact={true}
          path={path.paymentBalance}
          component={routes.PaymentBalancePage}
        />
        <Route
          exact={true}
          path={path.paymentBalanceAnotherCard}
          component={() => <routes.PaymentBalancePage withCardFormOnly={true} />}
        />

        {/* Smart payment */}
        <Route
          exact={true}
          path={path.smartPayment}
          component={routes.SmartPaymentPage}
        />
        <Route
          exact={true}
          path={path.smartPaymentAnotherCard}
          component={() => <routes.SmartPaymentPage withCardFormOnly={true} />}
        />

        {/* Wallet */}
        <Route
          exact={true}
          path={path.myWallet}
          component={routes.MyWalletPage}
        />
        <Route
          exact={true}
          path={path.myWalletAddNewPaymentCard}
          component={() => (
            <routes.AddNewPaymentCardPage
              title={t`Додавання карти`}
            />
          )}
        />

        {/* Checkout */}
        <Route
          path={[
            path.checkoutAuthLogin,
            path.checkoutAuthRegistration,
          ]}
          component={routes.CheckoutAuthRoutes}
        />

        <Route
          path={path.checkoutAuthRedirect}
          component={routes.CheckoutAuthRedirectPage}
        />

        <Route
          exact={true}
          path={path.checkoutBuyer}
          component={routes.CheckoutPage}
        />
        <Route
          exact={true}
          path={path.checkoutPayment}
          component={routes.CheckoutPaymentPage}
        />
        <Route
          exact={true}
          path={path.myDiscountPromotions}
          component={routes.DiscountPromotionsPage}
        />
        <Route
          exact={true}
          path={path.myDiscountPromotionsParticipates}
          component={routes.DiscountPromotionsParticipatesPage}
        />
        <Route
          exact={true}
          path={path.myDiscountPromotionsProductsSelect}
          component={routes.DiscountPromotionProductsListPage}
        />
        <Route
          exact={true}
          path={path.checkoutPaymentAnotherCard}
          component={() => <routes.CheckoutPaymentPage withCardFormOnly={true} />}
        />
        <Route
          exact={true}
          path={path.checkoutAddNewPaymentCard}
          component={() => (
            <routes.AddNewPaymentCardPage
              title={t`Оплата замовлення`}
              tip={t`Якщо не прийдете за посилкою або відмовитеся від товару у відділенні,
            то гроші повернемо вам на картку (за вирахуванням вартості доставки в один бік)`}
            />
          )}
        />
        <Route
          exact={true}
          path={path.checkoutPaymentStatus}
          component={routes.CheckoutPaymentStatusPage}
        />
        <Route
          exact={true}
          path={path.servicePaymentStatus}
          component={routes.ServicePaymentStatusPage}
        />

        <Route
          path={path.checkoutSeller}
          component={routes.CheckoutSellerPage}
        />
        <Route
          path={path.mySalesIndex}
          component={routes.MySalesPage}
        />
        <Route
          path={path.myPurchasesIndex}
          component={routes.MyPurchasesPage}
        />
        <Route
          path={path.collections}
          component={routes.CollectionPage}
        />
        <Route
          path={path.looksCollection}
          component={routes.LooksCollectionPage}
        />
        <Route
          path={path.looksCollections}
          component={routes.LooksCollectionsPage}
        />
        <Route
          path={path.dayDiscount}
          component={() => <routes.CollectionPage slug="skidka-dnya" />}
        />
        <Route
          path={path.newArrivals}
          component={() => <routes.CollectionPage slug="novinki" />}
        />
        <Route
          path={path.swapCollection}
          exact={true}
          component={() => <routes.CollectionPage slug="swap-party" />}
        />
        <Route
          path={path.productReviews}
          component={routes.ProductReviewsPage}
        />

        <Route
          path={NonSpaPath.productDetails}
          component={routes.ProductDetailsPage}
        />

        <Route
          path={path.baseCatalog}
          component={routes.CatalogPage}
        />

        <Route
          path={path.cardVerificationCallback}
          component={routes.CardVerificationCallbackPage}
        />
        <Route
          exact={true}
          path={path.mySubscribedBrandsProducts}
          component={routes.SubscribedBrandsProducts}
        />
        <Route
          exact={true}
          path={path.mySubscribedUsersProducts}
          component={routes.SubscribedUsersProducts}
        />
        <Route
          exact={true}
          path={path.mySubscribedSavedSearch}
          component={routes.SubscribedSavedSearch}
        />
        <Route
          exact={true}
          path={path.savedSearchRedirect}
          component={routes.RedirectToViewedPage}
        />
        <Route
          exact={true}
          path={path.autoSavedSearchRedirect}
          component={routes.RedirectToViewedPage}
        />
        <Route
          exact={true}
          path={path.phoneVerification}
          component={routes.PhoneVerificationPage}
        />
        <Route
          path={path.virtualCatalog}
          component={routes.VirtualCatalogPage}
        />

        <Route
          path={[
            NonSpaPath.catalogBlock,
            NonSpaPath.catalog,
          ]}
          component={routes.CatalogReviews}
        />

        <Route
          path={path.bankIdFailDuplicateVerification}
          component={routes.BankIdFailDuplicate}
        />
        <Route
          path={path.bankIdFailVerification}
          component={routes.BankIdFailVerification}
        />
        <Route
          path={path.bankIdSuccessVerification}
          component={routes.BankIdSuccessVerification}
        />

        <Route
          path={path.mobileAppsLanding}
          component={routes.MobileAppsLandingPage}
        />
        <Route
          path={path.safePaymentLanding}
          component={routes.SafePaymentLandingPage}
        />
        <Route
          path={path.smartLanding}
          component={routes.SmartLandingPage}
        />
        <Route
          path={path.swapLanding}
          component={routes.SwapLandingPage}
        />

        {/* Always should be before path.secondaryPage */}
        <Route
          path={path.contacts}
          render={() => <routes.ContactsPage slug="kontakty" />}
        />
        <Route
          path={path.secondaryPage}
          render={({ match }) => <routes.SecondaryPage slug={match.params.slug} />}
        />
        <Route
          path={path.termsOfService}
          component={() => <routes.PublicContractPage slug="terms-of-service" />}
        />
        <Route
          path={path.privacyPolicy}
          component={() => <routes.PublicContractPage slug="privacy-policy" />}
        />
        <Route
          path={path.socialAddPostThanks}
          component={routes.SocialAddPostThanks}
        />
        <Route
          path={path.social}
          component={routes.SocialRoutes}
        />
        <Route
          path={path.swapBuyerCheckout}
          component={routes.SwapBuyerCheckoutPage}
        />
        <Route
          path={path.swapSellerCheckout}
          component={routes.SwapSellerCheckoutPage}
        />
        <Route
          path={path.swapBuyerCheckoutThanks}
          component={routes.SwapNewOrderThanksPage}
        />

        <Route
          path={path.bankAccountIdentification}
          component={routes.BankAccountIdentificationPage}
          exact={true}
        />
        <Route
          path={path.bankAccountIdentificationPending}
          component={routes.BankAccountIdentificationPendingPage}
        />
        <Route
          path={path.myFavorites}
          component={routes.MyFavoritesPage}
        />
        <Route
          path={[
            path.addNewProduct,
            path.editProduct,
          ]}
          component={routes.ProductFormPage}
        />

        <Route
          path={path.smart}
          component={routes.SmartRoutes}
        />

        <Route
          path={path.brands}
          component={routes.BrandsFeedPage}
        />
      </Switch>
      <Route
        render={({ history }) => (
          history.location.hash === NonSpaPath.deliverySettingsUpdate
        && (
          <DeliveryFlowForSellersContainer
            isOpen={true}
            onClose={() => history.replace({ hash: '' })}
          />
        )
        )}
      />
    </>
  );
};

export default Routes;
